import {
    IAdminUser,
    IEvent,
    ISelfUser,
    ITemplateBarcode,
    ITemplateText,
    ITicketTemplate,
    IVKGroup,
    ColorType,
    PositionType,
    SizeType,
    BBoxType,
} from 'store/slices/types';
import getApiClient from './apiClient';

const client = (): ReturnType<typeof getApiClient> => getApiClient('token');

export interface UserCreatePayload {
    url?: string;
    event_id?: number;
    username?: string;
    password?: string;
    is_staff?: boolean;
    is_superuser?: boolean;
    first_name?: string;
    last_name?: string;
    middle_name?: string;
}

export interface UserPatchPayload {
    first_name?: string | null;
    last_name?: string | null;
    middle_name?: string | null;
    username?: string | null;
    event_id?: number | null;
    is_staff?: boolean;
    is_superuser?: boolean;
}

export interface TextPatchPayload {
    size?: number;
    color?: ColorType;
    position?: PositionType;
    rotation?: number;
    font?: string;
}

export interface BarcodePatchPayload {
    type?: string;
    size?: SizeType;
    position?: PositionType;
    rotation?: number;
    bbox?: BBoxType;
}

export interface VKGroupPatchPayload {
    name?: string;
    vk_id?: number;
    token?: string;
    confirmation_code?: string;
}

export interface EventPatchPayload {
    name?: string;
    on_start_message?: string | null;
    on_not_found_message?: string | null;
    on_scan_message?: string | null;
}

export interface EventCreatePayload {
    name: string;
    on_start_message?: string | null;
    on_not_found_message?: string | null;
    on_scan_message?: string | null;
    vk_group: {
        name: string;
        vk_id: number;
        token: string;
        confirmation_code: string;
    };
}

export async function getMe(): Promise<ISelfUser> {
    const { data } = await client().get<ISelfUser>('/users/me/');
    return data;
}

export async function listUsers(): Promise<IAdminUser[]> {
    const { data } = await client().get<IAdminUser[]>('/users/');
    return data;
}

export async function createUser(
    payload: UserCreatePayload
): Promise<IAdminUser> {
    const { data } = await client().post<IAdminUser>('/users/', payload);
    return data;
}

export async function bulkCreateUsers(
    payload: UserCreatePayload[]
): Promise<IAdminUser[]> {
    const { data } = await client().post<IAdminUser[]>('/users/many/', payload);
    return data;
}

export async function patchUser(
    id: number,
    payload: UserPatchPayload
): Promise<IAdminUser> {
    const { data } = await client().patch<IAdminUser>(`/users/${id}/`, payload);
    return data;
}

export async function deleteUser(id: number): Promise<void> {
    await client().delete(`/users/${id}/`);
}

export async function reissueTicket(id: number): Promise<void> {
    await client().post(`/users/${id}/reissue-ticket/`);
}

export async function resetPassword(
    id: number,
    newPassword: string
): Promise<IAdminUser> {
    const { data } = await client().post<IAdminUser>(
        `/users/${id}/reset-password/`,
        // eslint-disable-next-line camelcase
        { new_password: newPassword }
    );
    return data;
}

export async function listEvents(): Promise<IEvent[]> {
    const { data } = await client().get<IEvent[]>('/events/');
    return data;
}

export async function createEvent(
    payload: EventCreatePayload
): Promise<IEvent> {
    const { data } = await client().post<IEvent>('/events/', payload);
    return data;
}

export async function updateEvent(
    id: number,
    payload: EventPatchPayload
): Promise<IEvent> {
    const { data } = await client().put<IEvent>(`/events/${id}/`, payload);
    return data;
}

export async function getVKGroup(eventId: number): Promise<IVKGroup> {
    const { data } = await client().get<IVKGroup>(
        `/events/${eventId}/vk-group/`
    );
    return data;
}

export async function patchVKGroup(
    eventId: number,
    payload: VKGroupPatchPayload
): Promise<IVKGroup> {
    const { data } = await client().patch<IVKGroup>(
        `/events/${eventId}/vk-group/`,
        payload
    );
    return data;
}

export async function getTemplate(id: number): Promise<ITicketTemplate> {
    const { data } = await client().get<ITicketTemplate>(`/templates/${id}/`);
    return data;
}

export async function patchTemplateText(
    textId: number,
    payload: TextPatchPayload
): Promise<ITemplateText> {
    const { data } = await client().patch<ITemplateText>(
        `/templates/texts/${textId}/`,
        payload
    );
    return data;
}

export async function patchTemplateBarcode(
    barcodeId: number,
    payload: BarcodePatchPayload
): Promise<ITemplateBarcode> {
    const { data } = await client().patch<ITemplateBarcode>(
        `/templates/barcodes/${barcodeId}/`,
        payload
    );
    return data;
}

export async function uploadTemplateBackground(
    templateId: number,
    file: File
): Promise<ITicketTemplate> {
    const form = new FormData();
    form.append('ticket_file', file);
    const { data } = await client().post<ITicketTemplate>(
        `/templates/${templateId}/file/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
}

export async function uploadTextFont(
    templateId: number,
    textId: number,
    file: File
): Promise<ITemplateText> {
    const form = new FormData();
    form.append('font', file);
    const { data } = await client().post<ITemplateText>(
        `/templates/${templateId}/texts/${textId}/font/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
}

export async function listFonts(): Promise<string[]> {
    const { data } = await client().get<string[]>('/templates/fonts/');
    return data;
}

export async function uploadFont(file: File): Promise<string[]> {
    const form = new FormData();
    form.append('font', file);
    const { data } = await client().post<string[]>('/templates/fonts/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
}

export interface IEventAsset {
    id: number;
    event_id: number;
    kind: string;
    filename: string;
    vk_doc_id: string | null;
}

export async function listEventAssets(eventId: number): Promise<IEventAsset[]> {
    const { data } = await client().get<IEventAsset[]>(
        `/events/${eventId}/assets/`
    );
    return data;
}

export async function uploadEventAsset(
    eventId: number,
    kind: 'map' | 'works_order',
    file: File
): Promise<IEventAsset> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await client().post<IEventAsset>(
        `/events/${eventId}/assets/${kind}/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
}

export interface IVoteResult {
    work_id: string;
    work_name: string;
    count: number;
}

export interface IVoteResults {
    total: number;
    results: IVoteResult[];
}

export async function getVoteResults(eventId: number): Promise<IVoteResults> {
    const { data } = await client().get<IVoteResults>(
        `/events/${eventId}/votes/`
    );
    return data;
}

export async function previewTemplate(
    templateId: number,
    payload: { texts?: string[]; barcode?: string } = {}
): Promise<Blob> {
    const { data } = await client().post<Blob>(
        `/templates/${templateId}/preview/`,
        payload,
        { responseType: 'blob' }
    );
    return data;
}
